import { Box, HStack, Table, Tbody, Td, Text, Tr } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "react-feather";
import { customCurrencyFormat } from "../../../utils";

const labelOverride = {"Advertising & Marketing": "Marketing", "Overheads": "Overhead", "Practice Costs" : "Practice Cost"}

const ProfitLossTable = ({
  title = "",
  accounts = [],
  listed = true,
  givenTotal,
}) => {
  const getAllAccountIds = (accountsList) => {
    const ids = {};

    const traverse = (accounts) => {
      accounts.forEach((account) => {
        ids[account.id] = true; // mark as open
        if (account.children && account.children.length > 0) {
          traverse(account.children);
        }
      });
    };

    traverse(accountsList);
    return ids;
  };

  const [openRows, setOpenRows] = useState(() => getAllAccountIds(accounts));
  const [isSectionOpen, setIsSectionOpen] = useState(true);

  const allAccountsZero = accounts?.every(
    (account) => account?.total === 0 || account?.total === "0.00"
  );

  const toggleRow = (id) => {
    setOpenRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const toggleSection = () => {
    setIsSectionOpen((prev) => !prev);
  };

  const prevOpenRowsRef = useRef({});

  useEffect(() => {
    const nextOpenRows = getAllAccountIds(accounts);
    const prevOpenRows = prevOpenRowsRef.current;

    const currentKeys = Object.keys(prevOpenRows);
    const nextKeys = Object.keys(nextOpenRows);

    const isSame =
      currentKeys.length === nextKeys.length &&
      currentKeys.every((key) => prevOpenRows[key] === nextOpenRows[key]);

    if (!isSame) {
      prevOpenRowsRef.current = nextOpenRows;
      setOpenRows(nextOpenRows);
    }
  }, [accounts]);

  const sectionTotal = accounts?.reduce(
    (acc, item) => acc + parseFloat(item.total || 0),
    0
  );

  const renderAccountRow = (account, depth = 1) => {
    const hasChildren = account?.children && account?.children?.length > 0;
    const isOpen = openRows[account?.id];

    return (
      <React.Fragment key={account.id}>
        {account?.total !== 0 && account?.total !== "0.00" && (
          <Tr
            fontSize="14px"
            fontWeight={hasChildren || depth === 0 ? 500 : 500}
            textColor={!hasChildren && depth !== 0 && "#4A5568"}
          >
            <Td padding={hasChildren || depth === 0 ? "3px 10px" : "2px 10px"}>
              <HStack ml={`${depth * 10}px`}>
                {hasChildren ? (
                  isOpen ? (
                    <ChevronUp
                      size={20}
                      onClick={() => toggleRow(account?.id)}
                      cursor="pointer"
                    />
                  ) : (
                    <ChevronDown
                      size={20}
                      onClick={() => toggleRow(account?.id)}
                      cursor="pointer"
                    />
                  )
                ) : (
                  <div style={{ width: 20 }} />
                )}
                <Text>{`${labelOverride[account?.name] || account?.name}`}</Text>
              </HStack>
            </Td>

            {/* Show total in the same format if sub-head is collapsed */}
            <Td
              textAlign="right"
              padding={hasChildren ? "3px 30px" : "2px 30px"}
              fontWeight={hasChildren && !isOpen ? 800 : 400}
            >
              {!hasChildren
                ? customCurrencyFormat(account?.total, false, true, false, true)
                : !isOpen
                ? customCurrencyFormat(account?.total, false, true, false, true)
                : ""}
            </Td>
          </Tr>
        )}

        {/* Render children only if parent row is open */}
        {isOpen &&
          hasChildren &&
          account.children.map((child) => renderAccountRow(child, depth + 1))}

        {/* Render subtotal only if open */}
        {isOpen &&
          hasChildren &&
          account?.total !== 0 &&
          account?.total !== "0.00" && (
            <Tr fontSize="14px" fontWeight={800} borderTop="1px solid grey">
              <Td pl={`${(depth + 1) * 10 + 38}px`} py="3px" pr="10px">
                <Text>Total {`${labelOverride[account?.name] || account?.name}`}</Text>
              </Td>
              <Td textAlign="right" padding="3px 30px">
                {customCurrencyFormat(account?.total, false, true, true, true)}
              </Td>
            </Tr>
          )}
      </React.Fragment>
    );
  };

  return (
    (listed || !allAccountsZero) && (
      <Box overflowX="auto">
        <Table variant="unstyled" textColor="#1A202C">
          <Tbody>
            {/* SECTION HEADER WITH COLLAPSE TOGGLE */}
            <Tr
              backgroundColor="white"
              textColor="#1A202C"
              fontSize="14px"
              fontWeight={500}
            >
              <Td fontSize={"14px"} padding={"2px 10px"}>
                <HStack>
                  {isSectionOpen ? (
                    <ChevronUp
                      size={20}
                      cursor="pointer"
                      onClick={toggleSection}
                    />
                  ) : (
                    <ChevronDown
                      size={20}
                      cursor="pointer"
                      onClick={toggleSection}
                    />
                  )}
                  <Text>{title}</Text>
                </HStack>
              </Td>
              <Td
                textAlign="right"
                fontSize={"14px"}
                padding="2px 30px"
                fontWeight={isSectionOpen ? 400 : 800}
              >
                {!isSectionOpen &&
                  customCurrencyFormat(
                    givenTotal ? givenTotal : sectionTotal,
                    false,
                    true,
                    false,
                    true
                  )}
              </Td>
            </Tr>

            {isSectionOpen &&
              [...accounts]
                .map((account) => renderAccountRow(account))}

            {
              <Tr borderTop="1px solid grey" fontSize="14px" fontWeight={800}>
                <Td padding={"2px 10px"}>Total {title}</Td>
                <Td textAlign="right" padding={"2px 30px"}>
                  {customCurrencyFormat(
                    givenTotal ? givenTotal : sectionTotal,
                    false,
                    true,
                    true,
                    true
                  )}
                </Td>
              </Tr>
            }
          </Tbody>
        </Table>
      </Box>
    )
  );
};

export default ProfitLossTable;
